import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import {
  catchError,
  filter,
  first,
  mergeMap,
  switchMap,
  take,
} from 'rxjs/operators';
import { LogoutSuccess } from './+state/auth.actions';
import { AuthState } from './+state/auth.reducer';
import { getTokens } from './+state/auth.selectors';
import { AuthService } from './auth.service';
import { addToken, CUSTOMER_LOGOUT, isNonAuthEndpoint } from './token-helper';
import { LocationService } from '../public/location.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );

  constructor(
    public authService: AuthService,
    private store: Store<AuthState>,
    private injector: Injector,
  ) {}

  /**
   * Lazy on purpose: `LocationService` pulls in `WhitelabelService`, which
   * injects `HttpClient` directly. A constructor injection here would make
   * building `HttpClient`'s interceptor chain depend on `LocationService`
   * depend on `HttpClient` again — a DI cycle (`NG0200`). Resolving through
   * the injector at call time, after the app's injectors are already up,
   * breaks it — the same trick `LocationService` itself uses for its own
   * `StoreService` cycle.
   */
  private getLocationService(): LocationService {
    return this.injector.get(LocationService);
  }

  /**
   * The backoffice session has ended; `LocationService` picks the login of the
   * UI on screen (card #11440). The customer session is not this interceptor's
   * to end: `CustomerTokenInterceptor` owns 401s on the customer's own token
   * (card #11561).
   */
  private navigateToLogin(): void {
    this.getLocationService().navigateToLogin();
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.store.select(getTokens).pipe(
      first(),
      mergeMap((tokens) => {
        if (isNonAuthEndpoint(request)) {
          // Card #11561: a 401 on a request that carried no backoffice JWT
          // cannot mean the backoffice session expired, so only a request this
          // interceptor attached the JWT to is refreshed or logged out here.
          // Story #11691: the customer's own logout never gets the backoffice
          // JWT, or in admin order capture it would revoke the admin's session
          // instead of the customer's. CustomerTokenInterceptor attaches the
          // customer's.
          const attachedBackofficeJwt =
            !!tokens && !!tokens.jwt && !request.context.get(CUSTOMER_LOGOUT);
          if (attachedBackofficeJwt) {
            request = addToken(request, tokens.jwt);
          }
          return next.handle(request).pipe(
            catchError((error) => {
              if (!error.errors) {
                error.errors = [];
              }
              if (!!error.error && !error.error.errors) {
                error.error.errors = [];
              }
              if (
                attachedBackofficeJwt &&
                error instanceof HttpErrorResponse &&
                error.status === 401
              ) {
                return this.handle401Error(request, next, error);
              } else {
                return throwError(error);
              }
            }),
          );
        } else {
          return next.handle(request);
        }
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse,
  ) {
    if (!this.authService.isRefreshingToken) {
      this.refreshTokenSubject.next(null);

      return this.store.select(getTokens).pipe(
        first(),
        mergeMap((tokens) => {
          if (!!tokens.refreshToken) {
            return this.authService.refreshToken(tokens.refreshToken).pipe(
              switchMap((token: any) => {
                this.refreshTokenSubject.next(token.jwt);
                return next.handle(addToken(request, token.jwt));
              }),
              catchError((error) => {
                if (!error.errors) {
                  error.errors = [];
                }
                if (!!error.error && !error.error.errors) {
                  error.error.errors = [];
                }
                if (
                  error instanceof HttpErrorResponse &&
                  error.status === 401
                ) {
                  this.store.dispatch(
                    new LogoutSuccess({
                      src: 'TokenInterceptor',
                      description: '401 error from refresh token',
                    }),
                  );
                  this.navigateToLogin();
                }
                return throwError(error);
              }),
            );
          } else {
            this.authService.isRefreshingToken = false;
            this.store.dispatch(
              new LogoutSuccess({
                src: 'TokenInterceptor',
                description: 'No refresh token',
              }),
            );
            this.navigateToLogin();
            // Card #11561: this callback used to fall off its end, so the caller
            // got a `TypeError` instead of the 401 it is waiting for.
            return throwError(() => originalError);
          }
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return next.handle(addToken(request, jwt));
        }),
      );
    }
  }
}
